/**
 * Small recursive key-diff for comparing a static example response against
 * a live "Try Live" call result. No diff library exists anywhere in this
 * codebase (confirmed by survey) — this is intentionally basic: object
 * keys are compared by name, arrays by index. Good enough to spot
 * "this field is missing in the live response" / "this field's value
 * changed" at a glance; not a byte-perfect structural diff.
 */
export type DiffStatus = 'same' | 'added' | 'removed' | 'changed';

export interface DiffNode {
  status: DiffStatus;
  left: unknown;
  right: unknown;
  children?: Record<string, DiffNode>;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function diffJson(left: unknown, right: unknown): DiffNode {
  if (left === undefined && right !== undefined) return { status: 'added', left, right };
  if (left !== undefined && right === undefined) return { status: 'removed', left, right };

  if (isPlainObject(left) && isPlainObject(right)) {
    const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)]));
    const children: Record<string, DiffNode> = {};
    let anyDiff = false;
    for (const key of keys) {
      const child = diffJson(left[key], right[key]);
      children[key] = child;
      if (child.status !== 'same') anyDiff = true;
    }
    return { status: anyDiff ? 'changed' : 'same', left, right, children };
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    const children: Record<string, DiffNode> = {};
    let anyDiff = left.length !== right.length;
    const len = Math.max(left.length, right.length);
    for (let i = 0; i < len; i++) {
      const child = diffJson(left[i], right[i]);
      children[String(i)] = child;
      if (child.status !== 'same') anyDiff = true;
    }
    return { status: anyDiff ? 'changed' : 'same', left, right, children };
  }

  const same = JSON.stringify(left) === JSON.stringify(right);
  return { status: same ? 'same' : 'changed', left, right };
}

export function hasDifferences(node: DiffNode): boolean {
  return node.status !== 'same';
}

export interface DiffEntry {
  path: string;
  status: DiffStatus;
  left: unknown;
  right: unknown;
}

/** Leaf-level differences only (skips 'changed' object/array parents once their children are listed), for a compact summary. */
export function summarizeDiff(node: DiffNode, prefix = ''): DiffEntry[] {
  if (node.status === 'same') return [];
  if (!node.children) return [{ path: prefix || '(root)', status: node.status, left: node.left, right: node.right }];

  const out: DiffEntry[] = [];
  for (const [key, child] of Object.entries(node.children)) {
    const path = prefix ? `${prefix}.${key}` : key;
    out.push(...summarizeDiff(child, path));
  }
  return out;
}
